// Per-device online indicator — mirrors the Flutter getStatus():
//   • green dot        → reported within the last 60s (online)
//   • red dot + "Nm"   → offline; N = minutes since it last reported
//
// TWO CLOCKS, WHICH IS THE WHOLE DIFFICULTY HERE.
//
// The server's timestamps are its own local time (IST) labelled UTC, so every
// row reads five and a half hours in the future — see core/devices/deviceStatus.
// This component used to compute `Date.now() - seen` directly, which made that
// difference NEGATIVE and therefore always under the 60-second threshold: EVERY
// DEVICE SHOWED GREEN, permanently, whether or not it was there. A dot that is
// always green is worse than no dot, because it is believed.
//
// So server stamps and live sightings are kept apart and aged separately:
//   • a server stamp goes through ageOf(), which applies the learned offset
//   • a live MQTT message is on OUR clock, and needs no correction at all
// The device is as recent as the better of the two.
//
// ⚠️ NOT EVERY LIST ROW CARRIES A TIMESTAMP, and the ones that do disagree on
// its name:
//
//   room devices     lastUpdatedDateTime   (lowercase l)
//   common devices   LastUpdatedDateTime   (capital L)
//   GetListofDevicesbyUserId   — no time field at all
//
// A tile fed from that last one can never show anything from the row, so this
// falls back to the status endpoint, which always has it. Without that the
// indicator is simply absent, which reads as "the feature is broken".

import React, { useEffect, useRef, useState } from 'react';
import { Box, CircularProgress, Tooltip, Typography } from '@mui/material';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import {
  ageOf, ONLINE_WINDOW_MS, getLastSeenCached, hasNoHeartbeat, getContactAges,
} from '../../../core/devices/deviceStatus';
import {
  probeOne, confirmLive, SURE_ONLINE_MS, STALE_ASK_MS, PROBE_WINDOW_MS,
} from '../../../core/devices/liveness';
import { lastHeardAt, lastOfflineAt, addMessageListener } from '../../../core/mqtt/mqttClient';

// ⚠️ GREEN = "HEARD FROM IN 20 MINUTES", NOT "WILL TAKE A COMMAND" (2026-10-17).
// An ESP8684 posts a network ping every 3 minutes over HTTP even when its MQTT
// link is down — 16091206 was green and would not switch. So the dot can be
// CLICKED: it asks the device itself (core/devices/liveness probeOne, an S05
// read) and shows "answered in 0.6 s" or "no answer" for a minute. A later live
// message from the device supersedes a failed check.
const PROBE_SHOWN_MS = 60000;
const ago = (ms) => {
  const m = Math.floor(ms / 60000);
  return m < 1 ? 'just now' : `${m} min ago`;
};

function parseTs(raw) {
  if (!raw) return null;
  const t = Date.parse(String(raw).trim().replace(' ', 'T'));
  return Number.isNaN(t) ? null : t;
}

// The red label: minutes the device has been OFFLINE (owner, 2026-10-20:
// "123 min = offline since 123 minutes"). Always minutes, never hours, and
// never the word "offline" — 0 min says the same and keeps every dot alike.
const offlineFor = (mins) => `${Math.max(0, mins)} min`;

/**
 * @param live  the CLOCK TIME a message arrived over MQTT (useDeviceStatus's
 *              third return value), or null. Changing it is what marks the
 *              device as heard-from just now.
 *
 * ⚠️ NOT THE STATUS STRING. Three cards used to pass `status`, which
 * useDeviceStatus seeds from the device row — so a value the server recorded
 * eleven days ago arrived on the FIRST RENDER and painted the dot green for
 * devices that were plainly off. A status string is a fact about the last
 * report; only its ARRIVAL is a fact about now.
 */
/** A BLE lamp group has no heartbeat — no dot at all (see hasNoHeartbeat). */
export default function DeviceStatusDot({ device, live }) {
  if (hasNoHeartbeat(device)) return null;
  return <HeartbeatDot device={device} live={live} />;
}

function HeartbeatDot({ device, live }) {
  const raw = device && (device.lastUpdatedDateTime ?? device.LastUpdatedDateTime);
  const serverStampRef = useRef(parseTs(raw));   // server's clock
  const liveAtRef = useRef(null);                // our clock
  const [, force] = useState(0);
  const tick = () => force((n) => n + 1);

  // A fresh row from the server → adopt its timestamp if newer.
  useEffect(() => {
    const t = parseTs(raw);
    if (!t) return;
    if (!serverStampRef.current || t > serverStampRef.current) {
      serverStampRef.current = t;
      tick();
    }
  }, [raw]);

  // The row had no timestamp — ask the status endpoint, which always has one.
  // Cached and shared, so a grid of tiles is one request per device, not one
  // per render.
  const uno = device && (device.UniqueNumber ?? device.uniqueNumber);
  const [fetchedAge, setFetchedAge] = useState(null);
  // When that age was measured, so the "minutes ago" keeps advancing between
  // refetches instead of freezing at whatever it was on arrival.
  const fetchedAtRef = useRef(0);
  useEffect(() => {
    if (raw || !uno) return undefined;
    let alive = true;
    getLastSeenCached(uno).then((age) => {
      if (!alive || age == null) return;
      fetchedAtRef.current = Date.now();
      setFetchedAge(age);
    });
    return () => { alive = false; };
  }, [raw, uno]);

  // A live MQTT message means the device reported just now — no clock skew is
  // involved, because we observed it ourselves. `live` IS that moment, so it is
  // used as given rather than re-stamped.
  useEffect(() => {
    if (live) { liveAtRef.current = Number(live) || Date.now(); tick(); }
  }, [live]);

  // Keep the "minutes ago" fresh.
  useEffect(() => {
    const id = setInterval(tick, 30000);
    return () => clearInterval(id);
  }, []);

  // The device's "Device Offline" last will — redraw at once (it turns red
  // below). Ordinary listeners never receive it (mqttClient.deliver).
  useEffect(() => {
    if (!uno) return undefined;
    const topic = `${uno}/client`;
    return addMessageListener((t) => { if (t === topic) tick(); }, { offlineNotices: true });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uno]);

  const fromServer = serverStampRef.current != null
    ? ageOf(serverStampRef.current) : Infinity;
  const fromLive = liveAtRef.current != null
    ? Date.now() - liveAtRef.current : Infinity;
  // The fetched age was correct when it arrived; it only grows from there.
  const fromFetch = fetchedAge != null ? fetchedAge + (Date.now() - fetchedAtRef.current) : Infinity;
  const diff = Math.min(fromServer, fromLive, fromFetch);

  // ── Check now (click) ────────────────────────────────────────────────────
  const [probe, setProbe] = useState(null);   // null | 'busy' | { ok, ms, at }
  const check = async (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (!uno || probe === 'busy') return;
    setProbe('busy');
    const r = await probeOne(uno).catch(() => ({ ok: null }));
    setProbe({ ...r, at: Date.now() });
  };
  useEffect(() => {
    if (!probe || probe === 'busy') return undefined;
    const id = setTimeout(() => setProbe(null), PROBE_SHOWN_MS);
    return () => clearTimeout(id);
  }, [probe]);
  // ── Would be green → ASK the device (liveness.confirmLive) ───────────────
  // The server's timestamps cannot say whether a command will land: the HTTP
  // network ping keeps them fresh with MQTT dead ("30 min ago" on the details
  // page, green here). So unless THIS page heard the device over MQTT in the last
  // 10 min, it is asked once (cached 3 min, shared by every card). No answer →
  // red with the minutes since the LAST CONTACT of any kind (a message heard,
  // the network ping, a status report — deviceStatus.getLastContactAge).
  // A reply also lands on the card's own listener (liveAt),
  // so a healthy device is not asked again for 10 minutes.
  const [grey, setGrey] = useState(null);   // null | true (answered) | false
  const [mqttAge, setMqttAge] = useState(null);
  const [pingAge, setPingAge] = useState(null);   // HTTP network ping — tooltip only
  // Heard by THIS app in the last 10 min — the card's own listener or any other
  // (the Message Box, another card): mqttClient.lastHeardAt.
  const heardMs = Math.max(liveAtRef.current || 0, (uno && lastHeardAt(uno)) || 0);
  const heardHere = heardMs > 0 && Date.now() - heardMs < SURE_ONLINE_MS;
  // ⚠️ ASKED ON BOTH SIDES OF THE WINDOW (2026-10-19). A fresh stamp can lie
  // one way (HTTP ping alive, MQTT dead); a stale one the other way — current
  // sensors (16, 82) seldom refresh it, so 801038 sat red "107m" mid-reading.
  // Anything heard from in the last week is asked; older, the stamp stands.
  const greyNow = Number.isFinite(diff) && diff < STALE_ASK_MS && !heardHere;
  const stale = Number.isFinite(diff) && diff >= ONLINE_WINDOW_MS;
  useEffect(() => {
    if (!greyNow || !uno) { setGrey(null); setMqttAge(null); setPingAge(null); return undefined; }
    let alive = true;
    confirmLive(uno).then((ok) => {
      if (!alive) return;
      setGrey(ok);
      if (ok === false) {
        getContactAges(uno).then((a) => {
          if (!alive) return;
          setMqttAge(a.mqtt);
          setPingAge(a.ping);
        });
      }
    });
    return () => { alive = false; };
  }, [greyNow, uno]);

  // A live message after a failed check proves the device is there after all.
  const shownProbe = probe && probe !== 'busy'
    && !(probe.ok === false && liveAtRef.current && liveAtRef.current > probe.at) ? probe : null;

  // Went offline (its last will) AFTER the last thing heard from it — and
  // after any answered check: red now, not when a timestamp ages out.
  const offlineMs = (uno && lastOfflineAt(uno)) || 0;
  const answeredAt = shownProbe && shownProbe.ok === true ? shownProbe.at : 0;
  const saidOffline = offlineMs > Math.max(heardMs, answeredAt);

  if (!Number.isFinite(diff) && !probe && !saidOffline) return null; // never seen → show nothing

  const heard = Number.isFinite(diff) ? `Last heard ${ago(diff)}` : 'Never heard';
  let dot;
  let tip;
  if (probe === 'busy') {
    dot = <CircularProgress size={10} thickness={6} />;
    tip = 'Asking the device…';
  } else if (saidOffline && !(shownProbe && shownProbe.ok === true)) {
    const mins = Math.floor((Date.now() - offlineMs) / 60000);
    dot = (
      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}>
        <FiberManualRecordIcon sx={{ fontSize: 11, color: 'error.main' }} />
        <Typography variant="caption" color="error.main" sx={{ lineHeight: 1, fontWeight: 700 }}>
          {offlineFor(mins)}
        </Typography>
      </Box>
    );
    tip = `Offline for ${mins} min — the broker reported it gone ("Device Offline"). `
      + 'Commands will not reach it. Click to ask it.';
  } else if (shownProbe && shownProbe.ok === true) {
    dot = (
      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}>
        <FiberManualRecordIcon sx={{ fontSize: 11, color: 'success.main' }} />
        <Typography variant="caption" color="success.main" sx={{ lineHeight: 1, fontWeight: 700 }}>✓</Typography>
      </Box>
    );
    tip = `Answered in ${(shownProbe.ms / 1000).toFixed(1)} s — it will take commands`;
  } else if (shownProbe && shownProbe.ok === false) {
    dot = (
      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}>
        <FiberManualRecordIcon sx={{ fontSize: 11, color: 'error.main' }} />
        <Typography variant="caption" color="error.main" sx={{ lineHeight: 1, fontWeight: 700 }}>no answer</Typography>
      </Box>
    );
    tip = `No answer in ${PROBE_WINDOW_MS / 1000} s — commands will not reach it. ${heard}. Click to ask again.`;
  } else if (shownProbe && shownProbe.ok === null) {
    dot = <FiberManualRecordIcon sx={{ fontSize: 11, color: 'text.disabled' }} />;
    tip = 'Could not check — the app is not connected to the broker.';
  } else if ((!stale && !(greyNow && grey === false)) || (stale && greyNow && grey === true)) {
    dot = <FiberManualRecordIcon sx={{ fontSize: 11, color: 'success.main' }} />;
    tip = stale
      ? `Answered just now. The server's last stamp is ${Math.floor(diff / 60000)} min old — this device seldom refreshes it.`
      : `${heard} — click to check it answers now`;
  } else {
    // Failed the live check: count from the last contact of any kind.
    // ⚠️ From the last MQTT contact, NEVER Math.min with `diff` — `diff` is the
    // HTTP ping, refreshed every 1–3 min with MQTT dead, which made every red dot
    // read 0–1 min ("ac" / "WASHROOM", 2026-10-21). `diff` only stands in when
    // there is no MQTT contact on record at all.
    const failed = greyNow && grey === false;
    const mins = Math.floor((failed && mqttAge != null ? mqttAge : diff) / 60000);
    const pingNote = failed && pingAge != null && pingAge < ONLINE_WINDOW_MS
      ? ` Its network ping came ${Math.floor(pingAge / 60000)} min ago — powered and on the internet, but not answering commands.`
      : '';
    dot = (
      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}>
        <FiberManualRecordIcon sx={{ fontSize: 11, color: 'error.main' }} />
        <Typography variant="caption" color="error.main" sx={{ lineHeight: 1, fontWeight: 700 }}>
          {offlineFor(mins)}
        </Typography>
      </Box>
    );
    tip = greyNow && grey === false
      ? `Offline for ${mins} min — not answering since it was last heard over MQTT.${pingNote} Commands will not reach it. Click to ask again.`
      : `${heard} — click to check now`;
  }
  return (
    <Tooltip title={tip} arrow>
      <Box
        component="span"
        role="button"
        onClick={check}
        sx={{ display: 'inline-flex', alignItems: 'center', cursor: 'pointer', lineHeight: 0 }}
      >
        {dot}
      </Box>
    </Tooltip>
  );
}
